/**
 * Shared registration inbox.
 *
 * The site has no database, so registrations submitted on someone else's
 * device cannot normally reach your console. Every submission is published to
 * a private shared channel that your console reads, which makes registrations
 * visible at /admin from any device.
 *
 * Requests are deliberately kept "simple" (no custom headers) so the browser
 * does not need a CORS preflight, which is what most commonly blocks this kind
 * of cross-origin call.
 *
 * Retention on the free channel is limited, so the console copies anything it
 * finds into its own permanent records on every sync. Registrations are also
 * emailed to the firm as a durable second copy.
 */

/** Private channel name. Treat it as a secret — anyone who knows it can read it. */
const CHANNEL = 'velnoris-exec-net-reg-7fb42a9c';
const BASE = `https://ntfy.sh/${CHANNEL}`;

export interface InboundRegistration {
  fullName: string;
  email: string;
  professionalTitle: string;
  yearsExperience: string;
  linkedInUrl: string;
  motivation: string;
  reference: string;
  submittedAt: string;
}

export interface InboxResult {
  records: InboundRegistration[];
  reachable: boolean;
  error?: string;
}

/** Called by the public form. Never blocks or breaks the submission. */
export async function publishRegistration(record: InboundRegistration): Promise<boolean> {
  const payload = JSON.stringify({ velnorisRegistration: record });
  try {
    /* No custom headers and a text/plain body — a "simple" CORS request. */
    const response = await fetch(BASE, { method: 'POST', body: payload });
    if (response.ok) return true;
  } catch {
    /* fall through to the beacon attempt below */
  }

  /* Last resort: fire-and-forget, survives the page being navigated away. */
  try {
    return navigator.sendBeacon?.(BASE, payload) ?? false;
  } catch {
    return false;
  }
}

function parseRecords(text: string): InboundRegistration[] {
  const results: InboundRegistration[] = [];
  for (const line of text.split('\n')) {
    if (!line.trim()) continue;
    try {
      const envelope = JSON.parse(line) as { event?: string; message?: string };
      if (envelope.event && envelope.event !== 'message') continue;
      if (!envelope.message) continue;
      const body = JSON.parse(envelope.message) as
        | { velnorisRegistration?: InboundRegistration }
        | InboundRegistration;
      const record = 'velnorisRegistration' in body ? body.velnorisRegistration : (body as InboundRegistration);
      if (record?.email && record?.fullName) results.push(record);
    } catch {
      /* Skip anything that is not one of our records. */
    }
  }
  return results;
}

/** Called by the console. Returns everything currently held on the channel. */
export async function fetchRegistrations(): Promise<InboxResult> {
  try {
    const response = await fetch(`${BASE}/json?poll=1&since=all`);
    if (!response.ok) return { records: [], reachable: false, error: `Inbox returned ${response.status}` };
    return { records: parseRecords(await response.text()), reachable: true };
  } catch (e) {
    return {
      records: [],
      reachable: false,
      error: e instanceof Error ? e.message : 'The shared inbox could not be reached.',
    };
  }
}
